
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import Button from '../../../../core/components/Generics/Button.vue';
import RequestDialog from '../../../../core/components/Generics/RequestDialog.vue';
import LoadedPolygonsTargetOption from '../../components/LoadedPolygonsTargetOption.vue';
import SwitchToggle from '../../../../core/components/Generics/SwitchToggle.vue';
import DecimalFormatTable from './DecimalFormatTable.vue';
import SexagesimalFormatTable from './SexagesimalFormatTable.vue';
import { FeatureCollection } from '../../../../shared/types/MapShapes';
import MapPolygons from '../../components/add_polygons_from_map/MapPolygons.vue';
import { convertDecimalListToSexagesimal, getCoordinatesFromFeature } from '../../../../core/utils/map-utils';

@Component({
  components: {
    Button,
    RequestDialog,
    LoadedPolygonsTargetOption,
    SwitchToggle,
    DecimalFormatTable,
    SexagesimalFormatTable,
    MapPolygons
  }
})
export default class CreatePolygonFromCoordinates extends Vue {
  @Prop({ default: null }) coordsEditCoordinates!: google.maps.Data.Feature | null;
  isSexagesimal = false;
  sexagesimalModeText = 'Formato Sexagesimal';
  decimalModeText = 'Formato Decimal';
  visible = true;
  modalTitle = 'Generar Polígono';
  subtitle = 'Ingrese las coordenadas:';
  overwriteGeneralMap = false;

  dialog = false;
  loading = false;
  success = false;
  error = false;
  dialogTitle = 'Guardando...';
  successMessage = 'Guardado correctamente.';
  errorMessage = 'Ocurrió un error.';

  showPolygonsOnMap = false;
  geojsonData: FeatureCollection | null = null;
  disabledReason = '';
  errorMessages: Record<string, string> = {
    decimal: '',
    sexagesimal: ''
  };

  get decimalTableRef() {
    return this.$refs.decimalTable as DecimalFormatTable;
  }

  get sexagesimalTableRef() {
    return this.$refs.sexagesimalTable as SexagesimalFormatTable;
  }

  get getDisabledReason() {
    return this.disabledReason;
  }

  get canSaveCoords(){
    return this.getDisabledReason === '';
  }

  get isEditingExistingPolygon() {
    return !!this.coordsEditCoordinates;
  }

  setDisabledReason(reason: string, type: string) {
    this.errorMessages[type] = reason;
    this.disabledReason = this.errorMessages[this.isSexagesimal ? 'sexagesimal' : 'decimal'];
  }

  closeDialog() {
    this.dialog = false;
    this.resetDialog();
  }

  resetDialog() {
    this.loading = false;
    this.success = false;
    this.error = false;
    this.dialogTitle = '';
  }

  previewGeojsonInMap(geoJson: FeatureCollection) {
    this.geojsonData = geoJson;
    this.showPolygonsOnMap = true;
  }

  onCoordenatesModeChanged(isSexagesimal: boolean) {
    this.isSexagesimal = isSexagesimal;

    if (!isSexagesimal) {
      this.sexagesimalTableRef.cancelChanges();

      // Ya vienen agrupados por ringIndex
      const grouped: google.maps.LatLngLiteral[][] = this.sexagesimalTableRef.getDecimalValuesFromTable();

      // Pasa directamente a decimalTable
      this.decimalTableRef.updateCoordinatesList(grouped);
    } else {
      this.decimalTableRef.cancelChanges();

      const sexagesimal = this.decimalTableRef.getCoordinatesInSexagesimal();
      this.sexagesimalTableRef.updateCoordinatesList(sexagesimal);
    }
  }


  clickSavePolygon() {

    let errorMsg = this.getDisabledReason;
    if(errorMsg !== '') {
      this.showMessage(errorMsg, 'error');
      return;
    }

    let geojsonInfo = this.geojsonData;
    if (!geojsonInfo) {
      geojsonInfo = this.isSexagesimal
        ? this.sexagesimalTableRef.exportToGeoJSON()
        : this.decimalTableRef.getGeoJsonData();
    }
    if (geojsonInfo) {
      const overwrite = this.isEditingExistingPolygon ? true : this.overwriteGeneralMap;
      this.changePolygonInMap(geojsonInfo, overwrite);
    }
  }

  mounted() {
    if (this.coordsEditCoordinates) {
      this.modalTitle = 'Modificar polígono';
      this.loadPolygonCoordinatesFromFeature(this.coordsEditCoordinates);
    }
  }

  loadPolygonCoordinatesFromFeature(feature: google.maps.Data.Feature) {
    const allCoords = getCoordinatesFromFeature(feature);
    const rings: google.maps.LatLngLiteral[][] = [];
    allCoords.forEach(multi =>
      multi.forEach(polygon =>
        polygon.forEach(ring => {
          const fixed = ring.map(pt => {
            if (Math.abs(pt.lat) > 90) {
              return { lat: pt.lng, lng: pt.lat };
            }
            return pt;
          });
          rings.push(fixed);
        })
      )
    );
    this.decimalTableRef.updateCoordinatesList(rings);

    const sexagesimalFormatted = rings.flatMap((ring, ringIndex) =>
      convertDecimalListToSexagesimal([ring]).map((coord, index) => ({
        latitude: coord.latitude,
        longitude: coord.longitude,
        isEditing: false,
        id: `${ringIndex + 1}-${index + 1}`,
        ringIndex
      }))
    );
    this.sexagesimalTableRef.updateCoordinatesList(sexagesimalFormatted);
  }

  @Emit() closeModal() {}
  @Emit() changePolygonInMap(newPolygon: FeatureCollection, overwrite: boolean = false) { this.closeModal(); }
  @Emit() showMessage(message: string, color: string) {}
}
